import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Robot, X, PaperPlaneRight, ArrowSquareOut, Sparkle } from '@phosphor-icons/react';
import AIActionRenderer from './AIActionRenderer';
import { AI_BUBBLE_EVENT } from './OnboardingTour';

// Hide the bubble on these paths (public, auth, onboarding, public apply, and the AI tabs themselves)
const HIDDEN_PATTERNS = [
  /^\/$/,
  /^\/login/,
  /^\/signup/,
  /^\/apply\//,
  /^\/accept-invite/,
  /^\/accept-team-invite/,
  /^\/verify-email/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/privacy/,
  /^\/terms/,
  /^\/vc\/chat$/,
  /^\/startup\/coach$/,
];

const AIBubble = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [bootLoading, setBootLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // The product tour opens the panel on its AI Coach step so you see the real
  // thing, then closes it again on the way out.
  useEffect(() => {
    const onToggle = (e) => setOpen(!!e.detail?.open);
    window.addEventListener(AI_BUBBLE_EVENT, onToggle);
    return () => window.removeEventListener(AI_BUBBLE_EVENT, onToggle);
  }, []);

  const role = user?.role; // 'vc' (also incubators) or 'startup'
  const isStartup = role === 'startup';
  const sendUrl = isStartup ? '/startup/coach/message' : '/vc/chat';
  const sessionsUrl = isStartup ? '/startup/coach/sessions' : '/vc/chat/sessions';
  const historyUrl = (sid) => isStartup ? `/startup/coach/history/${sid}` : `/vc/chat/history/${sid}`;
  const fullPagePath = isStartup ? '/startup/dashboard?ai=1' : '/vc/chat';

  const shouldHide = !user || !role || HIDDEN_PATTERNS.some(re => re.test(location.pathname));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // When bubble opens, resume the most recent session (so it's a continuation of the main chat tab).
  useEffect(() => {
    if (!open || shouldHide) return;
    const boot = async () => {
      setBootLoading(true);
      try {
        const res = await api.get(sessionsUrl);
        const sessions = res.data || [];
        if (sessions.length > 0) {
          const sid = sessions[0].session_id;
          const histRes = await api.get(historyUrl(sid));
          setMessages(histRes.data || []);
          setSessionId(sid);
        } else {
          setMessages([]);
          setSessionId('');
        }
      } catch (err) {
        // swallow — bubble keeps working with empty state
      } finally { setBootLoading(false); }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(m => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await api.post(sendUrl,
        { message: msg, session_id: sessionId || undefined }
      );
      const newSid = res.data.session_id;
      if (!sessionId && newSid) setSessionId(newSid);
      setMessages(m => [...m, { role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI failed');
      setMessages(m => m.slice(0, -1)); // rollback the user msg
      setInput(msg);
    } finally { setLoading(false); }
  };

  const openFullChat = () => { setOpen(false); navigate(fullPagePath); };

  if (shouldHide) return null;

  const accent = isStartup ? '#10b981' : '#6366f1'; // emerald for startup, indigo for VC/incubator

  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-5 z-50 w-14 h-14 rounded-full text-white shadow-lg hover:scale-110 transition-all flex items-center justify-center"
          style={{ backgroundColor: accent, boxShadow: `0 8px 24px -4px ${accent}80` }}
          data-testid="ai-bubble-fab"
          aria-label="Open AI assistant"
          title="Ask AI"
        >
          <Robot size={26} weight="fill" />
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-success border-2 border-background animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-20 right-5 z-50 w-[380px] max-w-[calc(100vw-1rem)] h-[600px] max-h-[calc(100vh-6rem)] bg-surface border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="ai-bubble-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border" style={{ background: `linear-gradient(135deg, ${accent}15, transparent)` }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                <Robot size={18} weight="fill" style={{ color: accent }} />
              </div>
              <div>
                <div className="text-sm font-heading font-semibold text-text-primary">{isStartup ? 'AI Coach' : 'AI Co-Pilot'}</div>
                <div className="text-[10px] text-text-muted">Continues your main chat</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={openFullChat} className="p-1.5 rounded hover:bg-background text-text-muted hover:text-text-primary" title="Open full chat" data-testid="ai-bubble-expand-btn">
                <ArrowSquareOut size={16} weight="bold" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded hover:bg-background text-text-muted hover:text-text-primary" data-testid="ai-bubble-close-btn">
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-background/50" data-testid="ai-bubble-messages">
            {bootLoading && <div className="text-center text-xs text-text-muted py-4">Loading your chat...</div>}
            {!bootLoading && messages.length === 0 && (
              <div className="text-center py-8 px-3">
                <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
                  <Sparkle size={20} weight="fill" style={{ color: accent }} />
                </div>
                <div className="text-sm font-medium text-text-primary mb-1">Hi {user?.full_name?.split(' ')[0] || 'there'} 👋</div>
                <div className="text-xs text-text-muted leading-relaxed">
                  {isStartup
                    ? 'Ask me anything about your metrics, fundraising, invites, or programs you applied to.'
                    : 'Ask me about your portfolio, applicants, programs — e.g. "shortlist top 10 applicants for Lab32 Cohort 14".'}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`ai-bubble-msg-${m.role}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.role === 'user' ? 'text-white' : 'bg-surface border border-border text-text-primary'}`}
                  style={m.role === 'user' ? { backgroundColor: accent } : {}}>
                  {m.role === 'user'
                    ? <div className="whitespace-pre-wrap">{m.content}</div>
                    : <AIActionRenderer content={m.content} />}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-2xl bg-surface border border-border text-xs text-text-muted">
                  Thinking<span className="inline-flex gap-0.5 ml-1">
                    <span className="w-1 h-1 rounded-full bg-text-muted animate-bounce" />
                    <span className="w-1 h-1 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <span className="w-1 h-1 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border bg-surface">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder={isStartup ? 'Ask your AI Coach...' : 'Ask anything about your portfolio...'}
                disabled={loading}
                className="flex-1 bg-background border border-border rounded-full px-4 py-2 text-sm text-text-primary focus:outline-none focus:border-primary disabled:opacity-50"
                data-testid="ai-bubble-input"
              />
              <button onClick={send} disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-full text-white flex items-center justify-center disabled:opacity-40 hover:scale-105 transition-transform"
                style={{ backgroundColor: accent }}
                data-testid="ai-bubble-send-btn">
                <PaperPlaneRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIBubble;
